import { FC, useState, MouseEvent, useEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'

/* Utils */
import { useThemeColors } from 'hooks/useThemeColors'
import { useFramework } from '../../../../../hooks/useFramework'
import { useOverallPerformanceHistory } from '../../../../../hooks/useOverallPerformanceHistory'

/* Components */
import { ScoreWrapper } from './overallPerformanceScore.styles'
import ViewToggle from './ViewToggle'
import CurrentScoreView from './CurrentScoreView'
import TrendView from './TrendView'
import { CategoryActivityItem } from '../../../../../models'
import dayjs from 'dayjs'
import { dateFormat } from '../../../../../lib/utils'

interface Props {
  performanceScore: number
  lastMonthChange?: number | null
  categories: CategoryActivityItem[]
}

type ViewType = 'current' | 'trend'

const OverallPerformanceScore: FC<Props> = ({ performanceScore, lastMonthChange, categories }) => {
  const getColor = useThemeColors()
  const [activeView, setActiveView] = useState<ViewType>('current')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { framework } = useFramework()
  const { historyData, loading, fetchHistoryData } = useOverallPerformanceHistory()

  useEffect(() => {
    if (activeView === 'trend' && framework) {
      fetchHistoryData(framework)
    }
  }, [activeView, framework, fetchHistoryData])

  const handleViewChange = (event: MouseEvent<HTMLElement>, newAlignment: string) => {
    setActiveView(newAlignment as ViewType)
  }

  const getLastUpdate = () => {
    if (categories.length > 0) {
      const data = categories[0].data

      if (data?.length > 0) {
        const lastValue = categories[0].data[data.length - 1].xValue || ''
        const nextMonday = dayjs(lastValue).weekday(8)

        return dayjs(nextMonday).format(dateFormat.lastUpdate)
      }
    }

    return dayjs().format(dateFormat.lastUpdate)
  }

  return (
    <ScoreWrapper className="overall-performance-score-wrapper" isExpanded={activeView === 'trend'}>
      <Box className="title-wrapper">
        <Box className="left-section">
          <Box className="title">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1.5 12.0001L6 7.50011L8.87095 10.3711C9.67341 8.79219 11.0028 7.46831 12.7468 6.69181L14.5739 5.87834M14.5739 5.87834L10.6131 4.35791M14.5739 5.87834L13.0535 9.83918"
                stroke={getColor('menu-item-text')}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Typography className="chart-title">Overall Program Score</Typography>
          </Box>
          <Typography className="chart-title-update">
            Last Update: <span>{getLastUpdate()}</span>
          </Typography>
        </Box>

        <ViewToggle activeView={activeView} onViewChange={handleViewChange} />
      </Box>

      <Box className="content" ref={containerRef}>
        {activeView === 'current' ? (
          <CurrentScoreView performanceScore={performanceScore} lastMonthChange={lastMonthChange} />
        ) : (
          <TrendView historyData={historyData} loading={loading} containerRef={containerRef} />
        )}
      </Box>
    </ScoreWrapper>
  )
}

export default OverallPerformanceScore
